import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Read/write URL query parameters with a stable API.
 * Use for syncing pagination/filter state to the URL so refresh + back-button work.
 */
export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    (key: string): string | null => searchParams.get(key),
    [searchParams],
  );

  const getNumber = useCallback(
    (key: string, fallback: number): number => {
      const raw = searchParams.get(key);
      const n = raw === null ? NaN : Number(raw);
      return Number.isFinite(n) ? n : fallback;
    },
    [searchParams],
  );

  const set = useCallback(
    (updates: Record<string, string | number | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === undefined || v === "") next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const all = useMemo(() => {
    const obj: Record<string, string> = {};
    searchParams.forEach((v, k) => {
      obj[k] = v;
    });
    return obj;
  }, [searchParams]);

  return { get, getNumber, set, all };
}
